import type { HostSurface } from './host-surface.server.ts'

/**
 * Product brand per branded surface (issues 46/47).
 *
 * MOST PAGES DO NOT NEED THE MAP. A route already knows its own audience:
 * `_staff/*` is only ever the staff console, `/verification/*` only ever the
 * customer wizard, `/prescriber/*` only ever the decision portal — host gating
 * guarantees it. Those files should import the brand they mean **directly**
 * (`VERIFY_MY_ORDER` / `DIMPLE_VERIFY`) rather than resolving anything at
 * request time.
 *
 * `BRAND_BY_SURFACE` exists for the ONE case that genuinely can't be known at
 * build time: `/`, the neutral landing that every surface is allowed to serve
 * (see `isPathAllowedOnSurface`) — one route file, three hosts — plus the root
 * `<title>` fallback that covers routes setting no title of their own.
 *
 * Note the `import type`: `host-surface.server.ts` is server-only, and a VALUE
 * import from a `.server.ts` into a module a component uses fails at request
 * time without tsc catching it. Type-only imports are erased at build.
 */

export type Brand = {
	name: string
	tagline: string
}

/**
 * The verifymyorder.com brand — staff console + prescriber portal. Named for
 * the domain so an external doctor or CS agent reads a service, not jargon.
 *
 * DISPLAY ONLY. The legacy "VML" labels are DATA and stay verbatim wherever
 * they are stored — `AppSetting.vmlEnabled`, event-log values, Shopify tags
 * (see the app CLAUDE.md non-negotiable). Nothing here renames a record.
 */
export const VERIFY_MY_ORDER: Brand = {
	name: 'Verify My Order',
	tagline: 'Prescription verification for contact-lens orders.',
}

/** The dimplecolor.com brand — customer wizard + the embedded Shopify app. */
export const DIMPLE_VERIFY: Brand = {
	name: 'Dimple Verify',
	tagline: 'Prescription verification service for Dimple Color.',
}

/**
 * Shared-route resolution only — see the module note above before reaching for
 * this. `internal` (*.fly.dev) is an address rather than a brand; it keeps the
 * Dimple brand as the neutral default.
 */
export const BRAND_BY_SURFACE: Record<HostSurface, Brand> = {
	staff: VERIFY_MY_ORDER,
	prescriber: VERIFY_MY_ORDER,
	customer: DIMPLE_VERIFY,
	internal: DIMPLE_VERIFY,
}
